'use client'

import { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { X } from 'lucide-react'
import { toast } from "@/lib/toast"
import { useCart, useAuth } from "@/lib/commerce/hooks"
import { getLoyaltyVouchersApi } from "@/lib/commerce/services/loyalty-api"
import { formatPrice, formatImageURL, formatDateTimeNormal, isCouponActive, isGiftCardActive, isLoyaltyActive } from "@/utils/util"
import { navigateUrl, sendClientRequest } from "@/utils/client-request"
import { UnderlineTabs } from "../elements/Field"
import { panelFocusRing } from "../elements/SidePanel"

type TabKey = 'coupon' | 'giftcard' | 'loyalty'

const removeLink = `text-[13px] text-muted-foreground underline-offset-4 transition-colors duration-150 hover:text-foreground hover:underline ${panelFocusRing}`

function CodeField({ placeholder, value, onChange, onApply, loading, label }: { placeholder: string, value: string, onChange: (value: string) => void, onApply: () => void, loading: boolean, label: string }) {
  return (
    <div className="flex h-11 border border-border focus-within:border-foreground transition-colors duration-150">
      <input aria-label={label} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onApply() } }} disabled={loading} className="flex-1 min-w-0 h-full px-3 bg-transparent border-0 text-sm placeholder:text-muted-foreground focus:ring-0 focus:outline-none disabled:opacity-50" />
      <button type="button" onClick={onApply} disabled={loading} className={`shrink-0 px-5 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 ${panelFocusRing}`}>
        {loading ? 'Шалгаж байна…' : 'Хэрэглэх'}
      </button>
    </div>
  )
}

function WarningNote({ children }: { children: React.ReactNode }) {
  return <p className="border-l border-foreground pl-4 text-xs leading-relaxed text-muted-foreground">{children}</p>
}

export default function DiscountTabs({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const [couponCode, setCouponCode] = useState('')
  const [giftCardCode, setGiftCardCode] = useState('')
  const [isLoadingCoupon, setIsLoadingCoupon] = useState(false)
  const [isLoadingGiftCard, setIsLoadingGiftCard] = useState(false)
  const [couponCondition, setCouponCondition] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<TabKey | null>(null)

  const { appliedCoupon, applyCoupon, removeCoupon, couponDiscount, appliedGiftCard, applyGiftCard, removeGiftCard, appliedLoyaltyVoucher, applyLoyaltyVoucher, removeLoyaltyVoucher, amount, sale, allProducts } = useCart()
  const { session, isLoggedIn } = useAuth()
  const [loyaltyVouchers, setLoyaltyVouchers] = useState<any[]>([])
  const [loyaltyProgram, setLoyaltyProgram] = useState<any>(null)

  const subtotal = amount - sale
  const loyaltyEnabled = isLoyaltyActive(vendor) && isLoggedIn

  useEffect(() => {
    if (!loyaltyEnabled) return
    let cancelled = false
    getLoyaltyVouchersApi({ vendorId: vendor?.id || vendor, session: session || undefined }).then(({ data }) => {
      if (!cancelled && Array.isArray(data)) setLoyaltyVouchers(data.filter((voucher: any) => voucher.status === 'active'))
    }).catch(() => {})
    sendClientRequest({ url: '/loyalty/tiers', auth: false, headers: { Vendor: vendor?.id || vendor } }).then(({ data }) => {
      if (!cancelled && data) setLoyaltyProgram(data)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [loyaltyEnabled])

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Хөнгөлөлтийн кодоо оруулна уу')
      return
    }
    setIsLoadingCoupon(true)
    setCouponCondition(null)
    try {
      const result = await applyCoupon(couponCode.trim())
      if (result?.success) {
        toast.success('Хөнгөлөлтийн код амжилттай идэвхжлээ')
        setCouponCode('')
        setCouponCondition(null)
      } else if (result?.condition) {
        setCouponCondition(result.condition)
      } else {
        toast.error(result?.error || 'Хөнгөлөлтийн код буруу эсвэл хугацаа дууссан байна')
      }
    } catch (error) {
      toast.error('Алдаа гарлаа')
      console.error(error)
    } finally {
      setIsLoadingCoupon(false)
    }
  }

  const handleRemoveCoupon = () => {
    removeCoupon()
    setCouponCondition(null)
    toast.success('Хөнгөлөлтийн код хасагдлаа')
  }

  const getCouponInfo = () => {
    if (!appliedCoupon) return null
    const discount = appliedCoupon.discount || appliedCoupon

    let discountText = ''
    let isExceeding = false

    if (discount.type === 'productDiscount' || discount.type === 'order') {
      let valueType = discount.valueType
      let valuePercent = discount.valuePercent
      let valueAmount = discount.valueAmount

      if (!valueType && discount.type === 'productDiscount' && discount.code && allProducts?.length) {
        for (const p of allProducts) {
          const pd = p.product || p
          const match = (p.discounts || pd?.discounts)?.find((d: any) => d.type === 'productDiscount' && d.method === 'code' && d.code === discount.code)
          if (match) {
            valueType = match.valueType
            valuePercent = match.valuePercent
            valueAmount = match.valueAmount
            break
          }
        }
      }

      if (valueType === 'percent' && valuePercent) {
        discountText = `${valuePercent}% хямдрал`
      } else if (valueType === 'amount' && valueAmount) {
        discountText = `${formatPrice(valueAmount)} хямдрал`
        isExceeding = valueAmount > subtotal
      } else if (valueType === 'free') {
        discountText = 'Үнэгүй'
      }
    } else if (discount.type === 'buyXgetY') {
      let matchMode = discount.buyXMatchMode
      let triggerCount = discount.triggerTargets?.length || 1
      let minQty = discount.buyXMinQuantity || 1
      let maxReward = discount.getYMaxQuantity || 1

      if (!matchMode && discount.id && allProducts?.length) {
        for (const p of allProducts) {
          if (p.isRewardItem) continue
          const pd = p.product || p
          const match = (p.discounts || pd?.discounts)?.find((d: any) => d.type === 'buyXgetY' && d.id === discount.id)
          if (match) {
            matchMode = match.buyXMatchMode
            triggerCount = match.triggerTargets?.length || 1
            minQty = match.buyXMinQuantity || 1
            maxReward = match.getYMaxQuantity || 1
            break
          }
        }
      }

      const buyCount = matchMode === 'all' ? triggerCount * minQty : minQty
      discountText = `${buyCount} авбал ${maxReward} бэлэг`
    } else if (discount.type === 'freeShipping' || appliedCoupon.freeShipping) {
      discountText = 'Үнэгүй хүргэлт'
    }

    if (!discountText && couponDiscount > 0) discountText = `${formatPrice(couponDiscount)} хямдрал`

    return { discountText: discountText || 'Хөнгөлөлт идэвхжсэн', isExceeding, discountName: discount.name || appliedCoupon.name }
  }

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) {
      toast.error('Бэлгийн картын кодыг оруулна уу')
      return
    }
    setIsLoadingGiftCard(true)
    try {
      const success = await applyGiftCard(giftCardCode.trim())
      if (success) {
        toast.success('Бэлгийн карт амжилттай идэвхжлээ')
        setGiftCardCode('')
      } else {
        toast.error('Бэлгийн картын код буруу эсвэл хэрэглэгдсэн байна')
      }
    } catch (error) {
      toast.error('Алдаа гарлаа')
      console.error(error)
    } finally {
      setIsLoadingGiftCard(false)
    }
  }

  const handleRemoveGiftCard = () => {
    removeGiftCard()
    toast.success('Бэлгийн карт хасагдлаа')
  }

  const giftCardAmount = Number(appliedGiftCard?.amount) || 0
  const isGiftCardExceedsCart = giftCardAmount > subtotal
  const couponInfo = getCouponInfo()
  const appliedVoucherAmount = Number(appliedLoyaltyVoucher?.remainingAmount ?? appliedLoyaltyVoucher?.amount) || 0
  const voucherExceedsCart = appliedVoucherAmount > subtotal

  const couponContent = (
    <div className="space-y-4">
      {appliedCoupon && couponInfo ? (
        <>
          <div className="flex items-start justify-between gap-4 p-4 border border-foreground">
            <div className="min-w-0">
              <div className="text-sm">{couponInfo.discountName}</div>
              <div className="mt-1 text-xs text-muted-foreground">{couponInfo.discountText}</div>
              <div className="mt-2 flex items-center gap-3 text-xs">
                <span className="font-mono tracking-wide">{(appliedCoupon.discount || appliedCoupon)?.code}</span>
                {couponDiscount > 0 && <span className="tabular-nums">-{formatPrice(couponDiscount)}</span>}
              </div>
            </div>
            <button type="button" onClick={handleRemoveCoupon} className={removeLink}>Хасах</button>
          </div>
          {couponInfo.isExceeding && (
            <WarningNote>Хөнгөлөлтийн дүн нь сагсны дүн ({formatPrice(subtotal)})-ээс их байна. Та сагсандаа дахин бараа нэмж, хөнгөлөлтийг бүрэн ашиглана уу.</WarningNote>
          )}
        </>
      ) : (
        <>
          <CodeField label="Хөнгөлөлтийн код" placeholder="Хөнгөлөлтийн код оруулах" value={couponCode} onChange={setCouponCode} onApply={handleApplyCoupon} loading={isLoadingCoupon} />
          {couponCondition && (
            <div className="p-4 border border-border">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm">{couponCondition.discountName}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {couponCondition.type === 'minOrderAmount'
                      ? `${formatPrice(couponCondition.required)} захиалгад ${formatPrice(couponCondition.required - couponCondition.current)} дутуу байна`
                      : `${couponCondition.required} ширхэгээс ${couponCondition.required - couponCondition.current} дутуу байна`}
                  </div>
                </div>
                <button type="button" onClick={() => setCouponCondition(null)} aria-label="Хаах" className={`-mr-1 -mt-1 size-7 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${panelFocusRing}`}>
                  <X className="size-4" strokeWidth={1.5} />
                </button>
              </div>
              {couponCondition.triggerProducts?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {couponCondition.triggerProducts.map((p: any) => (
                    <Link key={p.id} href={navigateUrl(`/product/${p.customId}`, pathname)} className={`flex items-center gap-2 pr-3 border border-border transition-colors duration-150 hover:border-foreground ${panelFocusRing}`}>
                      {p.image && <img src={formatImageURL(p.image)} alt="" className="w-8 aspect-3/4 object-cover bg-muted" />}
                      <span className="text-xs max-w-[120px] truncate">{p.name}</span>
                    </Link>
                  ))}
                </div>
              )}
              <div className="mt-4 h-0.5 bg-border">
                <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${Math.min(100, (couponCondition.current / couponCondition.required) * 100)}%` }} />
              </div>
              <div className="mt-2 flex justify-between text-xs text-muted-foreground tabular-nums">
                <span>{couponCondition.type === 'minOrderAmount' ? formatPrice(couponCondition.current) : `${couponCondition.current} ширхэг`}</span>
                <span>{couponCondition.type === 'minOrderAmount' ? formatPrice(couponCondition.required) : `${couponCondition.required} ширхэг`}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )

  const giftCardContent = (
    <div className="space-y-4">
      {appliedGiftCard ? (
        <>
          <div className="flex items-center justify-between gap-4 p-4 border border-foreground">
            <div className="min-w-0">
              <div className="text-sm tabular-nums">Карт #{appliedGiftCard.customId}</div>
              <div className="mt-1 text-xs text-muted-foreground tabular-nums">{formatPrice(giftCardAmount)} хөнгөлөлт</div>
            </div>
            <button type="button" onClick={handleRemoveGiftCard} className={removeLink}>Хасах</button>
          </div>
          {isGiftCardExceedsCart && (
            <WarningNote>Бэлгийн картын дүн ({formatPrice(giftCardAmount)}) нь сагсны дүн ({formatPrice(subtotal)})-ээс их байна. Та сагсандаа дахин бараа нэмж, бэлгийн картыг ашиглана уу.</WarningNote>
          )}
        </>
      ) : (
        <CodeField label="Бэлгийн картын код" placeholder="Бэлгийн картын код" value={giftCardCode} onChange={setGiftCardCode} onApply={handleApplyGiftCard} loading={isLoadingGiftCard} />
      )}
    </div>
  )

  const loyaltyContent = (
    <div className="space-y-4">
      {appliedLoyaltyVoucher ? (
        <>
          <div className="flex items-center justify-between gap-4 p-4 border border-foreground">
            <div className="min-w-0">
              <div className="text-sm font-mono tracking-wide truncate">{appliedLoyaltyVoucher.code}</div>
              <div className="mt-1 text-xs text-muted-foreground">Идэвхжсэн</div>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              <span className="text-sm tabular-nums">-{formatPrice(appliedVoucherAmount)}</span>
              <button type="button" onClick={() => { removeLoyaltyVoucher(); toast.success('Ваучер хасагдлаа') }} className={removeLink}>Хасах</button>
            </div>
          </div>
          {!loyaltyProgram?.voucherPartialUse && voucherExceedsCart ? (
            <WarningNote>Ваучер захиалгад бүтнээрээ зарцуулагдана. Захиалгын дүн ваучерын дүнгээс бага тул үлдэх {formatPrice(appliedVoucherAmount - subtotal)} буцаагдахгүй.</WarningNote>
          ) : (
            <p className="text-xs text-muted-foreground">
              {loyaltyProgram?.voucherPartialUse ? 'Ашиглаагүй үлдэгдэл хадгалагдаж, дараагийн захиалгад ашиглагдана.' : 'Ваучер захиалгад бүтнээрээ зарцуулагдана.'}
            </p>
          )}
        </>
      ) : loyaltyVouchers.length > 0 ? (
        <ul className="divide-y divide-border border-y border-border">
          {loyaltyVouchers.map((voucher) => (
            <li key={voucher.id} className="flex items-center justify-between gap-4 py-4">
              <div className="min-w-0">
                <div className="text-sm font-mono tracking-wide truncate">{voucher.code}</div>
                <div className="mt-1 text-xs text-muted-foreground tabular-nums">{voucher.expireAt ? `${formatDateTimeNormal(voucher.expireAt)} хүртэл` : 'Хугацаагүй'}</div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="text-sm tabular-nums">{formatPrice(Number(voucher.remainingAmount ?? voucher.amount) || 0)}</span>
                <button type="button" onClick={() => { applyLoyaltyVoucher(voucher); toast.success('Ваучер идэвхжлээ') }} className={`h-9 px-4 border border-foreground text-xs tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background ${panelFocusRing}`}>Ашиглах</button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          Танд идэвхтэй ваучер алга байна.{' '}
          <Link href={navigateUrl('/loyalty', pathname)} className="text-foreground underline underline-offset-4">Оноогоо ваучер болгох</Link>
        </p>
      )}
    </div>
  )

  const canUseCoupon = isCouponActive(vendor)
  const canUseGiftCard = isGiftCardActive(vendor)
  const canUseLoyaltyVoucher = loyaltyEnabled && (loyaltyVouchers.length > 0 || !!appliedLoyaltyVoucher)

  const tabs: { value: TabKey, label: string, content: React.ReactNode }[] = [
    ...(canUseCoupon ? [{ value: 'coupon' as const, label: 'Хөнгөлөлтийн код', content: couponContent }] : []),
    ...(canUseGiftCard ? [{ value: 'giftcard' as const, label: 'Бэлгийн карт', content: giftCardContent }] : []),
    ...(canUseLoyaltyVoucher ? [{ value: 'loyalty' as const, label: 'Ваучер', content: loyaltyContent }] : []),
  ]

  if (!tabs.length) return null

  const current = tabs.find(t => t.value === activeTab) ?? tabs[0]

  return (
    <div className="mt-8">
      {tabs.length > 1 ? (
        <UnderlineTabs label="Хөнгөлөлт" options={tabs.map(t => ({ value: t.value, label: t.label }))} value={current.value} onChange={setActiveTab} />
      ) : (
        <div className="text-[11px] tracking-[0.14em] uppercase text-muted-foreground">{current.label}</div>
      )}
      <div className="mt-4">{current.content}</div>
    </div>
  )
}
